import { onMounted, onUnmounted, reactive, readonly, watch, type Ref } from 'vue'

export type VisualViewportBox = {
  width: number
  height: number
  offsetTop: number
  offsetLeft: number
  /** Space occluded at the bottom by the virtual keyboard (and similar UI). */
  keyboardOffset: number
}

export type UseVisualViewportOptions = {
  /**
   * Opt into the VirtualKeyboard API overlay mode for accurate keyboard insets
   * (used by bottom sheets). Leave off when you only need visualViewport size.
   */
  overlayKeyboard?: boolean
}

type VirtualKeyboard = {
  overlaysContent: boolean
  boundingRect: DOMRect
  addEventListener: (type: 'geometrychange', listener: () => void) => void
  removeEventListener: (type: 'geometrychange', listener: () => void) => void
}

function getVirtualKeyboard(): VirtualKeyboard | undefined {
  if (typeof navigator === 'undefined') return undefined
  return (navigator as Navigator & { virtualKeyboard?: VirtualKeyboard }).virtualKeyboard
}

/**
 * Tracks the visual viewport and keyboard occlusion.
 * When `enabled` is provided, listeners run only while it is true.
 */
export function useVisualViewport(
  enabled?: Ref<boolean>,
  options: UseVisualViewportOptions = {},
) {
  const state = reactive<VisualViewportBox>({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    offsetTop: 0,
    offsetLeft: 0,
    keyboardOffset: 0,
  })

  let listening = false
  let previousOverlaysContent: boolean | undefined

  const update = () => {
    const vv = window.visualViewport
    if (vv) {
      state.width = vv.width
      state.height = vv.height
      state.offsetTop = vv.offsetTop
      state.offsetLeft = vv.offsetLeft
    } else {
      state.width = window.innerWidth
      state.height = window.innerHeight
      state.offsetTop = 0
      state.offsetLeft = 0
    }

    const vvInset = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0
    const vkInset = getVirtualKeyboard()?.boundingRect.height ?? 0
    // Prefer the keyboard's own geometry when overlay mode is on. vvInset often
    // includes the gap above Chrome's bottom address bar.
    state.keyboardOffset = options.overlayKeyboard && vkInset > 0 ? vkInset : vvInset || vkInset
  }

  const start = () => {
    const vk = getVirtualKeyboard()
    if (options.overlayKeyboard && vk) {
      previousOverlaysContent = vk.overlaysContent
      vk.overlaysContent = true
      vk.addEventListener('geometrychange', update)
    }

    update()
    if (listening) return
    listening = true
    window.visualViewport?.addEventListener('resize', update)
    window.visualViewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
  }

  const stop = () => {
    const vk = getVirtualKeyboard()
    if (options.overlayKeyboard && vk) {
      vk.removeEventListener('geometrychange', update)
      if (previousOverlaysContent !== undefined) {
        vk.overlaysContent = previousOverlaysContent
        previousOverlaysContent = undefined
      }
    }

    if (!listening) return
    listening = false
    window.visualViewport?.removeEventListener('resize', update)
    window.visualViewport?.removeEventListener('scroll', update)
    window.removeEventListener('resize', update)
    state.keyboardOffset = 0
  }

  onMounted(() => {
    if (!enabled || enabled.value) start()
  })

  onUnmounted(stop)

  if (enabled) {
    watch(enabled, (isEnabled) => {
      if (isEnabled) start()
      else stop()
    })
  }

  return readonly(state)
}
